import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding, faMapPin, faSignOutAlt, faUserCircle, faShieldAlt, faDatabase } from '@fortawesome/free-solid-svg-icons';

// Sub-modules
import AdminSidebar from './AdminSidebar';
import AdminStats from './AdminStats';
import TurnTypeABM from './TurnTypeABM';
import UserABM from './UserABM';
import ScreenParams from './ScreenParams';
import EmpresaABM from './EmpresaABM';
import SucursalABM from './SucursalABM';
import InstallationManager from './InstallationManager';
import MySubscription from './MySubscription';
import SpecialtyABM from './SpecialtyABM';
import SpecialistABM from './SpecialistABM';
import BoxABM from './BoxABM';

// Styles & Tools
import './TurneroAdmin.css';
import request from '../../../../request';
import Notify from '../../../compartidos/Notify';

/**
 * TurneroAdminPanel - Premium Dashboard Overhaul
 * Features Collapsible Sidebar, Topbar with Glass Selectors, and Stats View.
 */
const TurneroAdminPanel = ({ ipserv }) => {
    const [activeTab, setActiveTab] = useState('dashboard');
    const [isCollapsed, setIsCollapsed] = useState(false);

    // State for tenant selection
    const [companies, setCompanies] = useState([]);
    const [allBranches, setAllBranches] = useState([]);
    const [branches, setBranches] = useState([]);
    const [selectedCompanyId, setSelectedCompanyId] = useState(localStorage.getItem('tufila_selected_company') || '');
    const [selectedBranchId, setSelectedBranchId] = useState(localStorage.getItem('tufila_selected_branch') || '');
    const [menuGroups, setMenuGroups] = useState([]);

    // Live Clock State
    const [currentTime, setCurrentTime] = useState(new Date());

    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/';

    // Load companies and branches
    useEffect(() => {
        request.Get(API_BASE + 'turnero/companies', {}, r => {
            if (r.success) setCompanies(r.data || []);
        });

        request.Get(API_BASE + 'turnero/branches', {}, r => {
            if (r.success) setAllBranches(r.data || []);
        });

        // Fetch dynamic menu
        request.Get(API_BASE + 'turnero/menu', {}, r => {
            if (r.success) setMenuGroups(r.data || []);
        });
    }, [API_BASE]);

    // Multi-tenant filtering logic
    useEffect(() => {
        if (selectedCompanyId && allBranches.length > 0) {
            const filteredBranches = allBranches.filter(b => String(b.EmpresaId) === String(selectedCompanyId));
            setBranches(filteredBranches);

            if (!selectedBranchId || !filteredBranches.find(b => String(b.SucursalId) === String(selectedBranchId))) {
                if (filteredBranches.length > 0) {
                    handleBranchChange(filteredBranches[0].SucursalId);
                } else {
                    handleBranchChange('');
                }
            }
        } else {
            setBranches([]);
        }
    }, [selectedCompanyId, allBranches]);

    const handleCompanyChange = (id) => {
        setSelectedCompanyId(id);
        localStorage.setItem('tufila_selected_company', id);
    };

    const handleBranchChange = (id) => {
        setSelectedBranchId(id);
        localStorage.setItem('tufila_selected_branch', id);
    };

    const handleLogout = () => {
        localStorage.clear();
        sessionStorage.clear();
        window.location.reload();
    };

    // Helper to render active module
    const renderContent = () => {
        const props = { ipserv, branch_id: selectedBranchId, hideHeader: true };

        // Static mapping for existing components
        const componentMap = {
            'dashboard': AdminStats,
            'empresa': EmpresaABM,
            'companies': EmpresaABM,    // Alias from MenuBuilder
            'sucursales': SucursalABM,
            'branches': SucursalABM,     // Alias from MenuBuilder
            'types': TurnTypeABM,
            'tipos': TurnTypeABM,
            'users': UserABM,
            'usuarios': UserABM,
            'params': ScreenParams,
            'parametros': ScreenParams,
            'installation': InstallationManager,
            'instalacion': InstallationManager,
            'subscription': MySubscription,
            'specialties': SpecialtyABM,
            'specialists': SpecialistABM,
            'boxes': BoxABM
        };

        const Component = componentMap[activeTab];

        if (Component) return <Component {...props} />;

        // Handle dynamic modules (e.g. ones configured in Menu Builder)
        // Check if it's a dynamic module from MenuBuilder
        const dynamicItem = menuGroups.flatMap(g => g.items).find(i => i.id === activeTab);
        if (dynamicItem && dynamicItem.type === 'DYNAMIC') {
            // In a more advanced version, we would use DynamicModuleResolver here
            return (
                <div className="sa-empty py-5">
                    <FontAwesomeIcon icon={faDatabase} style={{ fontSize: '3rem', opacity: 0.1, display: 'block', margin: '0 auto 1rem' }} />
                    <h5 className="text-muted">Módulo Dinámico: {dynamicItem.label}</h5>
                    <p className="small text-muted">Este módulo se resuelve vía Query Builder ({dynamicItem.id})</p>
                </div>
            );
        }

        return <div className="p-5 text-center text-muted">Módulo no encontrado o en desarrollo: {activeTab}</div>;
    };

    return (
        <div className="admin-layout">
            <AdminSidebar
                activeTab={activeTab}
                onTabChange={setActiveTab}
                isCollapsed={isCollapsed}
                toggleCollapse={() => setIsCollapsed(!isCollapsed)}
                menuGroups={menuGroups}
            />

            <main className="admin-main">
                <header className="admin-topbar">
                    <div className="topbar-selectors">
                        <div className="glass-selector">
                            <FontAwesomeIcon icon={faBuilding} className="text-primary" />
                            <select value={selectedCompanyId} onChange={(e) => handleCompanyChange(e.target.value)}>
                                <option value="" disabled>Seleccionar Empresa</option>
                                {companies.map(c => <option key={c.EmpresaId} value={c.EmpresaId}>{c.EmpresaNombre}</option>)}
                            </select>
                        </div>

                        <div className="glass-selector">
                            <FontAwesomeIcon icon={faMapPin} className="text-warning" />
                            <select value={selectedBranchId} onChange={(e) => handleBranchChange(e.target.value)}>
                                <option value="" disabled>Seleccionar Sucursal</option>
                                {branches.map(b => <option key={b.SucursalId} value={b.SucursalId}>{b.SucursalNombre}</option>)}
                            </select>
                        </div>
                    </div>

                    <div className="d-flex align-items-center gap-4">
                        <div className="d-none d-xl-flex flex-column align-items-end text-end mr-3">
                            <span className="small font-weight-bold" style={{ letterSpacing: '1px', textTransform: 'uppercase' }}>
                                {currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                            <span className="tiny text-muted">
                                {currentTime.toLocaleDateString([], { weekday: 'long', day: 'numeric', month: 'short' })}
                            </span>
                        </div>

                        <div className="d-flex align-items-center gap-3 pr-3 border-right border-light border-opacity-10">
                            <div className="position-relative">
                                <FontAwesomeIcon icon={faUserCircle} size="2x" className="text-muted opacity-50" />
                                <span className="status-indicator online"></span>
                            </div>
                            <div className="d-flex flex-column">
                                <span className="small font-weight-bold m-0 line-height-1">Administrador</span>
                                <span className="tiny text-success font-weight-bold" style={{ fontSize: '0.65rem' }}>EN LÍNEA</span>
                            </div>
                        </div>

                        <button className="turnero-btn turnero-btn-danger px-3 py-2" onClick={handleLogout} style={{ fontSize: '0.8rem' }}>
                            <FontAwesomeIcon icon={faSignOutAlt} /> Salir
                        </button>
                    </div>
                </header>

                <div className="admin-viewport">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={activeTab}
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            transition={{ duration: 0.3, ease: 'easeInOut' }}
                        >
                            {renderContent()}
                        </motion.div>
                    </AnimatePresence>

                    {!selectedBranchId && !['dashboard', 'empresa', 'companies', 'sucursales', 'branches', 'subscription'].includes(activeTab) && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="auth-card mt-4 p-3 text-center border-warning border-opacity-25"
                        >
                            <p className="text-warning m-0 small">
                                <FontAwesomeIcon icon={faMapPin} className="mr-2" />
                                Por favor selecciona una sucursal para gestionar sus datos.
                            </p>
                        </motion.div>
                    )}
                </div>
            </main>
        </div>
    );
};

export default TurneroAdminPanel;
